import { XCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import Image from 'next/image';

const COMPARISON_DATA: {
  positive: { title: string; description: string };
  negative: { description: string };
}[] = [
  {
    positive: {
      title: 'Sell faster',
      description: 'Connect with verified dealers and receive offers quickly.',
    },
    negative: {
      description: 'Wait on average of 4-6 weeks to sell your car privately.',
    },
  },
  {
    positive: {
      title: 'No time wasted',
      description:
        'Deal directly with trusted dealerships, avoiding lowball offers and tire kickers.',
    },
    negative: {
      description:
        'Time wasted with lowball offers, tire kickers, and strange requests.',
    },
  },
  {
    positive: {
      title: 'Safe and secure',
      description:
        'No need to meet strangers or arrange test drives; trusted dealers handle everything.',
    },
    negative: {
      description:
        'Risk and safety concerns when meeting with strangers and allowing test drives.',
    },
  },
  {
    positive: {
      title: 'Guaranteed payment',
      description: 'Receive secure payment from verified dealers.',
    },
    negative: {
      description: 'Anxiety over payment and falling victim to online scams.',
    },
  },
];

type ComparisonItemProps =
  | {
      isPositive: true;
      title: string;
      description: string;
    }
  | {
      isPositive: false;
      description: string;
    };

function ComparisonItem({
  description,
  isPositive,
  ...rest
}: ComparisonItemProps) {
  return (
    <div className="flex gap-l">
      <div className="mt-s shrink-0">
        {isPositive ? (
          <CheckCircleIcon
            stroke="transparent"
            fill="#010044"
            width={24}
            height={24}
          />
        ) : (
          <XCircleIcon stroke="white" fill="#B00C0C" height={24} width={24} />
        )}
      </div>
      <div>
        <p className="text-body1Light">
          {'title' in rest ? (
            <b className="text-body1Medium">{`${rest.title} -`}</b>
          ) : (
            ''
          )}{' '}
          {description}
        </p>
      </div>
    </div>
  );
}

export const ComparisonTable = () => (
  <div className="relative min-h-[600px] w-full">
    <div className="absolute inset-0 z-0">
      <Image
        src="/images/sell-my-car/comparison-bg.webp"
        alt="Mountain landscape with car"
        fill
        className="object-cover"
      />
    </div>
    <div className="relative z-10 my-auto flex h-full flex-row items-center justify-center py-[80px] l:ml-[60px] l:justify-start">
      <div className="max-w-4xl">
        <table className="w-full border-separate" cellSpacing={0}>
          <thead>
            <tr>
              <th className="w-1/2 rounded-tl-lg border-x-[1px] border-t-[1px] border-brandElectric bg-brandElectric p-l text-left">
                <h4 className="text-h4Regular">Sell My Car with EV.com</h4>
              </th>
              <th className="w-1/2 rounded-tr-lg bg-white p-l text-left">
                <h4 className="text-h4Regular">Selling Privately</h4>
              </th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_DATA.map(({ positive, negative }, index) => {
              const isLastRow = index === COMPARISON_DATA.length - 1;
              return (
                <tr key={`COMPARISON_TABLE_ROW_${index}`}>
                  <td
                    className={clsx(
                      'border-x-[1px] border-t-[1px] border-neutral-300 bg-green-light p-xl',
                      {
                        'rounded-bl-lg': isLastRow,
                      }
                    )}
                  >
                    <ComparisonItem {...positive} isPositive />
                  </td>
                  <td
                    className={clsx(
                      'border-r-[1px] border-t-[1px] border-neutral-300 bg-white p-xl',
                      {
                        'rounded-br-lg': isLastRow,
                      }
                    )}
                  >
                    <ComparisonItem {...negative} isPositive={false} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);
